import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from "@/services/api";

const API_BASE = getApiBaseUrl();

// White-label context for subdomain-based customization
const WhiteLabelContext = createContext(null);

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  return context || { isWhiteLabel: false, config: null, loading: false };
};

export const WhiteLabelProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [subdomain, setSubdomain] = useState(null);

  useEffect(() => {
    const detectSubdomain = async () => {
      try {
        const hostname = window.location.hostname;
        
        // Check if it's a subdomain of clarias.in
        // Format: subdomain.clarias.in or subdomain.preview.emergentagent.com (for testing)
        let detectedSubdomain = null;
        
        if (hostname.endsWith('.clarias.in')) {
          // Production: rakshaniias.clarias.in
          const parts = hostname.split('.');
          if (parts.length === 3 && parts[0] !== 'www') {
            detectedSubdomain = parts[0];
          }
        } else if (hostname.includes('.preview.emergentagent.com')) {
          // Preview environment - check URL param for testing
          const urlParams = new URLSearchParams(window.location.search);
          detectedSubdomain = urlParams.get('subdomain');
        } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
          // Local development - check URL param
          const urlParams = new URLSearchParams(window.location.search);
          detectedSubdomain = urlParams.get('subdomain');
        }

        if (detectedSubdomain) {
          console.log(`[WhiteLabel] Detected subdomain: ${detectedSubdomain}`);
          setSubdomain(detectedSubdomain);
          
          // Fetch config from API
          const response = await axios.get(`${API_BASE}/api/whitelabel/config?subdomain=${detectedSubdomain}`);
          
          if (response.data) {
            console.log('[WhiteLabel] Config loaded:', response.data.name);
            setConfig(response.data);
            setIsWhiteLabel(true);
            
            // Apply theme colors
            applyTheme(response.data);
          }
        } else {
          console.log('[WhiteLabel] No subdomain detected, using default CLARIAS');
        }
      } catch (error) {
        console.log('[WhiteLabel] Not a white-label subdomain or error:', error.message);
      } finally {
        setLoading(false);
      }
    };

    detectSubdomain();
  }, []);

  const applyTheme = (config) => {
    if (config.primary_color) {
      document.documentElement.style.setProperty('--whitelabel-primary', config.primary_color);
    }
    if (config.secondary_color) {
      document.documentElement.style.setProperty('--whitelabel-secondary', config.secondary_color);
    }
  };

  // Check if email is whitelisted for this subdomain
  const checkEmailWhitelisted = async (email) => {
    if (!isWhiteLabel || !subdomain) {
      return { allowed: true, reason: 'Not a white-label subdomain' };
    }

    try {
      const response = await axios.post(
        `${API_BASE}/api/whitelabel/check-email?subdomain=${subdomain}&email=${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error) {
      console.error('[WhiteLabel] Email check failed:', error);
      return { allowed: false, reason: 'Error checking email' };
    }
  };

  const value = {
    isWhiteLabel,
    config,
    subdomain,
    loading,
    checkEmailWhitelisted,
    // Helper functions
    shouldShowSignup: () => !isWhiteLabel || config?.access_control?.allow_signup,
    shouldShowPayment: () => !isWhiteLabel || config?.access_control?.allow_payment,
    shouldShowOptionalPayment: () => !isWhiteLabel || config?.access_control?.allow_optional_payment,
    getInstituteName: () => config?.name || 'CLARIAS',
    getInstituteLogo: () => config?.logo_url,
    getSupportPhone: () => config?.support_phone,
    getPoweredByConfig: () => config?.powered_by || { text: '', show_on_login: false, show_on_dashboard: false, show_on_settings: false }
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

// Powered By Badge Component
export const PoweredByBadge = ({ location = 'footer', className = '' }) => {
  const { isWhiteLabel, getPoweredByConfig } = useWhiteLabel();
  
  if (!isWhiteLabel) return null;
  
  const poweredBy = getPoweredByConfig();
  
  // Check if should show based on location
  if (location === 'login' && !poweredBy.show_on_login) return null;
  if (location === 'dashboard' && !poweredBy.show_on_dashboard) return null;
  if (location === 'settings' && !poweredBy.show_on_settings) return null;

  return (
    <div className={`flex items-center justify-center gap-2 text-slate-400 text-sm ${className}`}>
      <span>{poweredBy.text || 'Powered by CLARIAS Intelligence'}</span>
    </div>
  );
};

// Institute Header Component (shows institute name/logo)
export const InstituteHeader = ({ className = '' }) => {
  const { isWhiteLabel, getInstituteName, getInstituteLogo } = useWhiteLabel();
  
  if (!isWhiteLabel) return null;
  
  const name = getInstituteName();
  const logo = getInstituteLogo();

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {logo ? (
        <img src={logo} alt={name} className="h-10 w-auto" />
      ) : (
        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
          <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
        </div>
      )}
      <div>
        <h1 className="text-white font-bold text-xl">{name}</h1>
      </div>
    </div>
  );
};

export default WhiteLabelContext;
